import 'css/src/components/app/Waveform.module.scss';

import type { SyntheticEvent } from 'react';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import WaveformImage from 'js/src/components/app/waveform/WaveformImage';
import type { WaveformData } from 'js/src/components/app/waveform/waveform-types';

type WaveformPresentationProps = {
    waveformData: WaveformData | null;
    fillPercentage: number;
    onSeek?: (percent: number) => void;
    onResize?: (width: number, height: number) => void;
};

const WaveformPresentation = ({
    waveformData,
    fillPercentage,
    onSeek,
    onResize,
}: WaveformPresentationProps) => {
    const ref = useRef<HTMLDivElement | null>(null);

    const [width, setWidth] = useState(0);
    const [height, setHeight] = useState(0);

    const onClick = useCallback(
        (e: SyntheticEvent<HTMLDivElement, MouseEvent>) => {
            e.stopPropagation();
            if (!onSeek) {
                return;
            }
            const percent = Math.round((e.nativeEvent.offsetX / e.currentTarget.offsetWidth) * 100);
            onSeek(percent);
        },
        [onSeek],
    );

    useLayoutEffect(() => {
        if (!ref.current) {
            return;
        }
        const callback = () => {
            if (!ref.current) return;
            const newWidth = ref.current.offsetWidth;
            const newHeight = ref.current.offsetHeight;
            onResize?.(newWidth, newHeight);
            setWidth(newWidth);
            setHeight(newHeight);
        };
        callback();
        const observer = new ResizeObserver(callback);
        observer.observe(ref.current);
        return () => {
            observer.disconnect();
        };
    }, [ref, onResize]);

    let addBlankClass = true;
    let waveformImage = null;
    if (waveformData) {
        waveformImage = (
            <WaveformImage
                width={width}
                height={height}
                waveformData={waveformData}
                fillPercentage={fillPercentage}
            />
        );
        addBlankClass = waveformData.length === 0;
    }

    return (
        <div
            ref={ref}
            className={classNames('waveform cursor-pointer', {
                blank: addBlankClass,
            })}
            data-testid="waveform"
            data-blank={addBlankClass ? '' : undefined}
            onClick={onClick}
        >
            {waveformImage}
        </div>
    );
};

const WaveformMemoized = React.memo(WaveformPresentation);
const Waveform = WaveformMemoized;

export default Waveform;
